import React, { useState, useRef, useEffect } from 'react';
import api from '../../services/api';
import axios from 'axios';
import '../../styles/CMSManagement.css';

const CMSManagement: React.FC = () => {
  // Section collapsed state
  const [sectionsState, setSectionsState] = useState({
    promoVideo: true, // collapsed by default
    backgroundImage: true, // collapsed by default
    watermarkImage: true, // collapsed by default
    emailTemplate: true, // collapsed by default
  });

  // Toggle section collapsed state
  const toggleSection = (section: keyof typeof sectionsState) => {
    setSectionsState(prev => ({
      ...prev,
      [section]: !prev[section]
    }));
  };

  // Promo Video State
  const [selectedVideoFile, setSelectedVideoFile] = useState<File | null>(null);
  const [videoPreviewUrl, setVideoPreviewUrl] = useState<string | null>(null);
  const [videoLoading, setVideoLoading] = useState<boolean>(false);
  const [videoError, setVideoError] = useState<string | null>(null);
  const [videoSuccess, setVideoSuccess] = useState<string | null>(null);
  const [videoUploadProgress, setVideoUploadProgress] = useState<number>(0);
  const videoFileInputRef = useRef<HTMLInputElement>(null);

  // Background Image State
  const [selectedImageFile, setSelectedImageFile] = useState<File | null>(null);
  const [imagePreviewUrl, setImagePreviewUrl] = useState<string | null>(null);
  const [imageLoading, setImageLoading] = useState<boolean>(false);
  const [imageError, setImageError] = useState<string | null>(null);
  const [imageSuccess, setImageSuccess] = useState<string | null>(null);
  const [imageUploadProgress, setImageUploadProgress] = useState<number>(0);
  const imageFileInputRef = useRef<HTMLInputElement>(null);

  // Watermark Image State
  const [selectedWatermarkFile, setSelectedWatermarkFile] = useState<File | null>(null);
  const [watermarkPreviewUrl, setWatermarkPreviewUrl] = useState<string | null>(null);
  const [watermarkLoading, setWatermarkLoading] = useState<boolean>(false);
  const [watermarkError, setWatermarkError] = useState<string | null>(null);
  const [watermarkSuccess, setWatermarkSuccess] = useState<string | null>(null);
  const [watermarkUploadProgress, setWatermarkUploadProgress] = useState<number>(0);
  const watermarkFileInputRef = useRef<HTMLInputElement>(null);

  // Email Template State
  const [emailConfig, setEmailConfig] = useState({
    EMAIL_SUBJECT: 'Your Clipo Is Ready!',
    EMAIL_HEADING: 'Your Videos Are Ready!',
    EMAIL_THANK_YOU: 'Thank you for your purchase from Clipo! Your videos are now ready for download.',
    EMAIL_INSTRUCTIONS: 'This link provides direct access to your videos. You can download them to your device or share them with friends and family.',
    EMAIL_CLOSING: 'If you have any questions, please don\'t hesitate to contact our support team.',
    EMAIL_FOOTER: 'This is an automated email, please do not reply.'
  });
  const [emailConfigLoading, setEmailConfigLoading] = useState(false);
  const [emailConfigError, setEmailConfigError] = useState<string | null>(null);
  const [emailConfigSuccess, setEmailConfigSuccess] = useState<string | null>(null);

  // Load existing email config when component mounts
  useEffect(() => {
    loadEmailConfig();
  }, []);

  // Load the current email configuration
  const loadEmailConfig = async () => {
    try {
      setEmailConfigLoading(true);
      setEmailConfigError(null);
      
      // Fetch all configurations
      const response = await api.get('/admin/config');
      const configs = response.data;
      
      // Find the email config
      const emailConfigItem = configs.find((config: any) => config.key === 'CMS_EMAIL_COPY');
      
      if (emailConfigItem && emailConfigItem.value) {
        try {
          const parsedConfig = JSON.parse(emailConfigItem.value);
          setEmailConfig({
            EMAIL_SUBJECT: parsedConfig.EMAIL_SUBJECT || emailConfig.EMAIL_SUBJECT,
            EMAIL_HEADING: parsedConfig.EMAIL_HEADING || emailConfig.EMAIL_HEADING,
            EMAIL_THANK_YOU: parsedConfig.EMAIL_THANK_YOU || emailConfig.EMAIL_THANK_YOU,
            EMAIL_INSTRUCTIONS: parsedConfig.EMAIL_INSTRUCTIONS || emailConfig.EMAIL_INSTRUCTIONS,
            EMAIL_CLOSING: parsedConfig.EMAIL_CLOSING || emailConfig.EMAIL_CLOSING,
            EMAIL_FOOTER: parsedConfig.EMAIL_FOOTER || emailConfig.EMAIL_FOOTER
          });
        } catch (parseError) {
          console.error('Error parsing email config JSON:', parseError);
          setEmailConfigError('The stored email configuration is invalid. Default values will be used.');
        }
      }
    } catch (error) {
      console.error('Error loading email configuration:', error);
      setEmailConfigError('Failed to load email configuration. Please try again.');
    } finally {
      setEmailConfigLoading(false);
    }
  };

  // Handle email config field change
  const handleEmailConfigChange = (field: string, value: string) => {
    setEmailConfig(prev => ({
      ...prev,
      [field]: value
    }));
  };

  // Save email configuration
  const handleSaveEmailConfig = async () => {
    try {
      setEmailConfigLoading(true);
      setEmailConfigError(null);
      setEmailConfigSuccess(null);
      
      // Convert config object to JSON string
      const configValue = JSON.stringify(emailConfig);
      
      // Update configuration
      await api.put('/admin/config', {
        key: 'CMS_EMAIL_COPY',
        value: configValue
      });
      
      setEmailConfigSuccess('Email template configuration saved successfully!');
    } catch (error) {
      console.error('Error saving email configuration:', error);
      setEmailConfigError('Failed to save email configuration. Please try again.');
    } finally {
      setEmailConfigLoading(false);
    }
  };

  // Handle promo video file change
  const handleVideoFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setVideoError(null);
    setVideoSuccess(null);
    
    if (!e.target.files || e.target.files.length === 0) {
      setSelectedVideoFile(null);
      setVideoPreviewUrl(null);
      return;
    }
    
    const file = e.target.files[0];
    
    // Validate file type
    if (!file.type.startsWith('video/')) {
      setVideoError('Please select a valid video file');
      setSelectedVideoFile(null);
      setVideoPreviewUrl(null);
      return;
    }
    
    // Validate file size (max 100MB)
    if (file.size > 100 * 1024 * 1024) {
      setVideoError('Video file size must be less than 100MB');
      setSelectedVideoFile(null);
      setVideoPreviewUrl(null);
      return;
    }
    
    setSelectedVideoFile(file);
    
    // Create a preview URL for the video
    const fileURL = URL.createObjectURL(file);
    setVideoPreviewUrl(fileURL);
  };

  // Handle background image file change
  const handleImageFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setImageError(null);
    setImageSuccess(null);
    
    if (!e.target.files || e.target.files.length === 0) {
      setSelectedImageFile(null);
      setImagePreviewUrl(null);
      return;
    }
    
    const file = e.target.files[0];
    
    // Validate file type
    const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp'];
    if (!allowedTypes.includes(file.type)) {
      setImageError('Please select a valid image file (JPEG, PNG, or WebP)');
      setSelectedImageFile(null);
      setImagePreviewUrl(null);
      return;
    }
    
    // Validate file size (max 10MB)
    if (file.size > 10 * 1024 * 1024) {
      setImageError('Image file size must be less than 10MB');
      setSelectedImageFile(null);
      setImagePreviewUrl(null);
      return;
    }
    
    setSelectedImageFile(file);
    
    // Create a preview URL for the image
    const fileURL = URL.createObjectURL(file);
    setImagePreviewUrl(fileURL);
  };

  // Upload promo video
  const handleVideoUpload = async () => {
    if (!selectedVideoFile) {
      setVideoError('Please select a video file to upload');
      return;
    }
    
    setVideoLoading(true);
    setVideoError(null);
    setVideoSuccess(null);
    setVideoUploadProgress(0);
    
    const formData = new FormData();
    formData.append('video', selectedVideoFile);
    
    try {
      // Get the admin token for authorization
      const adminUser = localStorage.getItem('adminUser');
      let adminToken = '';
      
      if (adminUser) {
        try {
          const parsed = JSON.parse(adminUser);
          adminToken = parsed.token;
        } catch (e) {
          console.error('Error parsing admin user from localStorage:', e);
        }
      }
      
      // Create config for file upload with progress tracking and headers
      const config = {
        headers: {
          'Content-Type': 'multipart/form-data',
          'Authorization': `Bearer ${adminToken}`
        },
        onUploadProgress: (progressEvent: any) => {
          const percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total);
          setVideoUploadProgress(percentCompleted);
        },
        withCredentials: false // Set this to false to avoid CORS issues with credentials
      };
      
      // Log request for debugging
      console.log('Sending upload request to:', '/admin/cms/upload-promo-video');
      console.log('With file:', selectedVideoFile.name, selectedVideoFile.type, selectedVideoFile.size);
      console.log('Admin token present:', !!adminToken);
      
      // Direct axios call to avoid any API wrapper issues
      const baseURL = process.env.REACT_APP_API_URL || 'http://localhost:5001/api';
      console.log('Using base URL:', baseURL);
      
      // Try with standard axios first
      try {
        const response = await axios.post(
          `${baseURL}/admin/cms/upload-promo-video`, 
          formData, 
          config
        );
        
        // Reset form and show success message
        setVideoSuccess('Promo video uploaded successfully! The changes will be visible on the homepage after a refresh.');
        setSelectedVideoFile(null);
        setVideoPreviewUrl(null);
        if (videoFileInputRef.current) {
          videoFileInputRef.current.value = '';
        }
        
        console.log('Upload response:', response.data);
      } catch (firstError) {
        console.error('First upload attempt failed:', firstError);
        
        // If the first attempt fails, try with the api service
        try {
          console.log('Trying with api service...');
          const apiResponse = await api.post('/admin/cms/upload-promo-video', formData, {
            ...config,
            headers: {
              ...config.headers,
              // Don't set Content-Type here - axios will set it correctly with boundary
            }
          });
          
          // Reset form and show success message
          setVideoSuccess('Promo video uploaded successfully! The changes will be visible on the homepage after a refresh.');
          setSelectedVideoFile(null);
          setVideoPreviewUrl(null);
          if (videoFileInputRef.current) {
            videoFileInputRef.current.value = '';
          }
          
          console.log('Upload response from api service:', apiResponse.data);
        } catch (secondError) {
          throw secondError; // Re-throw to be caught by the outer catch
        }
      }
    } catch (err: any) {
      console.error('Error uploading video:', err);
      
      // Enhanced error handling
      if (err.response) {
        // The request was made and the server responded with a status code
        console.error('Server responded with:', err.response.status, err.response.data);
        const errorMessage = err.response.data?.message || 
                            `Error ${err.response.status}: ${err.response.statusText}`;
        setVideoError(errorMessage);
      } else if (err.request) {
        // The request was made but no response was received
        console.error('No response received from server');
        setVideoError('No response received from server. Please check your connection and try again.');
      } else {
        // Something happened in setting up the request
        console.error('Error setting up request:', err.message);
        setVideoError(`Request error: ${err.message}`);
      }
    } finally {
      setVideoLoading(false);
      setVideoUploadProgress(0);
    }
  };

  // Upload background image
  const handleImageUpload = async () => {
    if (!selectedImageFile) {
      setImageError('Please select an image file to upload');
      return;
    }
    
    setImageLoading(true);
    setImageError(null);
    setImageSuccess(null);
    setImageUploadProgress(0);
    
    const formData = new FormData();
    formData.append('image', selectedImageFile);
    
    try {
      // Get the admin token for authorization
      const adminUser = localStorage.getItem('adminUser');
      let adminToken = '';
      
      if (adminUser) {
        try {
          const parsed = JSON.parse(adminUser);
          adminToken = parsed.token;
        } catch (e) {
          console.error('Error parsing admin user from localStorage:', e);
        }
      }
      
      // Create config for file upload with progress tracking and headers
      const config = {
        headers: {
          'Content-Type': 'multipart/form-data',
          'Authorization': `Bearer ${adminToken}`
        },
        onUploadProgress: (progressEvent: any) => {
          const percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total);
          setImageUploadProgress(percentCompleted);
        },
        withCredentials: false // Set this to false to avoid CORS issues with credentials
      };
      
      // Log request for debugging
      console.log('Sending upload request to:', '/admin/cms/upload-bg-image');
      console.log('With file:', selectedImageFile.name, selectedImageFile.type, selectedImageFile.size);
      console.log('Admin token present:', !!adminToken);
      
      // Direct axios call to avoid any API wrapper issues
      const baseURL = process.env.REACT_APP_API_URL || 'http://localhost:5001/api';
      console.log('Using base URL:', baseURL);
      
      // Try with standard axios first
      try {
        const response = await axios.post(
          `${baseURL}/admin/cms/upload-bg-image`, 
          formData, 
          config
        );
        
        // Reset form and show success message
        setImageSuccess('Background image uploaded successfully! The changes will be visible after a refresh.');
        setSelectedImageFile(null);
        setImagePreviewUrl(null);
        if (imageFileInputRef.current) {
          imageFileInputRef.current.value = '';
        }
        
        console.log('Upload response:', response.data);
      } catch (firstError) {
        console.error('First upload attempt failed:', firstError);
        
        // If the first attempt fails, try with the api service
        try {
          console.log('Trying with api service...');
          const apiResponse = await api.post('/admin/cms/upload-bg-image', formData, {
            ...config,
            headers: {
              ...config.headers,
              // Don't set Content-Type here - axios will set it correctly with boundary
            }
          });
          
          // Reset form and show success message
          setImageSuccess('Background image uploaded successfully! The changes will be visible after a refresh.');
          setSelectedImageFile(null);
          setImagePreviewUrl(null);
          if (imageFileInputRef.current) {
            imageFileInputRef.current.value = '';
          }
          
          console.log('Upload response from api service:', apiResponse.data);
        } catch (secondError) {
          throw secondError; // Re-throw to be caught by the outer catch
        }
      }
    } catch (err: any) {
      console.error('Error uploading image:', err);
      
      // Enhanced error handling
      if (err.response) {
        // The request was made and the server responded with a status code
        console.error('Server responded with:', err.response.status, err.response.data);
        const errorMessage = err.response.data?.message || 
                            `Error ${err.response.status}: ${err.response.statusText}`;
        setImageError(errorMessage);
      } else if (err.request) {
        // The request was made but no response was received
        console.error('No response received from server');
        setImageError('No response received from server. Please check your connection and try again.');
      } else {
        // Something happened in setting up the request
        console.error('Error setting up request:', err.message);
        setImageError(`Request error: ${err.message}`);
      }
    } finally {
      setImageLoading(false);
      setImageUploadProgress(0);
    }
  };

  // Handle cancel upload for video
  const handleCancelVideoUpload = () => {
    setSelectedVideoFile(null);
    setVideoPreviewUrl(null);
    setVideoError(null);
    setVideoSuccess(null);
    if (videoFileInputRef.current) {
      videoFileInputRef.current.value = '';
    }
  };

  // Handle cancel upload for image
  const handleCancelImageUpload = () => {
    setSelectedImageFile(null);
    setImagePreviewUrl(null);
    setImageError(null);
    setImageSuccess(null);
    if (imageFileInputRef.current) {
      imageFileInputRef.current.value = '';
    }
  };

  // Handle watermark image file change
  const handleWatermarkFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setWatermarkError(null);
    setWatermarkSuccess(null);
    
    if (!e.target.files || e.target.files.length === 0) {
      setSelectedWatermarkFile(null);
      setWatermarkPreviewUrl(null);
      return;
    }
    
    const file = e.target.files[0];
    
    // Validate file type - only allow PNG for transparency
    const allowedTypes = ['image/png'];
    if (!allowedTypes.includes(file.type)) {
      setWatermarkError('Please select a valid PNG image for the watermark to support transparency');
      setSelectedWatermarkFile(null);
      setWatermarkPreviewUrl(null);
      return;
    }
    
    // Validate file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      setWatermarkError('Watermark file size must be less than 5MB');
      setSelectedWatermarkFile(null);
      setWatermarkPreviewUrl(null);
      return;
    }
    
    setSelectedWatermarkFile(file);
    
    // Create a preview URL for the image
    const fileURL = URL.createObjectURL(file);
    setWatermarkPreviewUrl(fileURL);
  };

  // Upload watermark image
  const handleWatermarkUpload = async () => {
    if (!selectedWatermarkFile) {
      setWatermarkError('Please select a PNG image to upload as watermark');
      return;
    }
    
    setWatermarkLoading(true);
    setWatermarkError(null);
    setWatermarkSuccess(null);
    setWatermarkUploadProgress(0);
    
    const formData = new FormData();
    formData.append('image', selectedWatermarkFile);
    
    try {
      // Get the admin token for authorization
      const adminUser = localStorage.getItem('adminUser');
      let adminToken = '';
      
      if (adminUser) {
        try {
          const parsed = JSON.parse(adminUser);
          adminToken = parsed.token;
        } catch (e) {
          console.error('Error parsing admin user from localStorage:', e);
        }
      }
      
      // Create config for file upload with progress tracking and headers
      const config = {
        headers: {
          'Content-Type': 'multipart/form-data',
          'Authorization': `Bearer ${adminToken}`
        },
        onUploadProgress: (progressEvent: any) => {
          const percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total);
          setWatermarkUploadProgress(percentCompleted);
        },
        withCredentials: false // Set this to false to avoid CORS issues with credentials
      };
      
      // Log request for debugging
      console.log('Sending upload request to:', '/admin/cms/upload-watermark');
      console.log('With file:', selectedWatermarkFile.name, selectedWatermarkFile.type, selectedWatermarkFile.size);
      console.log('Admin token present:', !!adminToken);
      
      // Direct axios call to avoid any API wrapper issues
      const baseURL = process.env.REACT_APP_API_URL || 'http://localhost:5001/api';
      console.log('Using base URL:', baseURL);
      
      // Try with standard axios first
      try {
        const response = await axios.post(
          `${baseURL}/admin/cms/upload-watermark`, 
          formData, 
          config
        );
        
        // Reset form and show success message
        setWatermarkSuccess('Watermark image uploaded successfully! The changes will be visible after a refresh.');
        setSelectedWatermarkFile(null);
        setWatermarkPreviewUrl(null);
        if (watermarkFileInputRef.current) {
          watermarkFileInputRef.current.value = '';
        }
        
        console.log('Upload response:', response.data);
      } catch (firstError) {
        console.error('First upload attempt failed:', firstError);
        
        // If the first attempt fails, try with the api service
        try {
          console.log('Trying with api service...');
          const apiResponse = await api.post('/admin/cms/upload-watermark', formData, {
            ...config,
            headers: {
              ...config.headers,
              // Don't set Content-Type here - axios will set it correctly with boundary
            }
          });
          
          // Reset form and show success message
          setWatermarkSuccess('Watermark image uploaded successfully! The changes will be visible after a refresh.');
          setSelectedWatermarkFile(null);
          setWatermarkPreviewUrl(null);
          if (watermarkFileInputRef.current) {
            watermarkFileInputRef.current.value = '';
          }
          
          console.log('Upload response from api service:', apiResponse.data);
        } catch (secondError) {
          throw secondError; // Re-throw to be caught by the outer catch
        }
      }
    } catch (err: any) {
      console.error('Error uploading watermark image:', err);
      
      // Enhanced error handling
      if (err.response) {
        // The request was made and the server responded with a status code
        console.error('Server responded with:', err.response.status, err.response.data);
        const errorMessage = err.response.data?.message || 
                            `Error ${err.response.status}: ${err.response.statusText}`;
        setWatermarkError(errorMessage);
      } else if (err.request) {
        // The request was made but no response was received
        console.error('No response received from server');
        setWatermarkError('No response received from server. Please check your connection and try again.');
      } else {
        // Something happened in setting up the request
        console.error('Error setting up request:', err.message);
        setWatermarkError(`Request error: ${err.message}`);
      }
    } finally {
      setWatermarkLoading(false);
      setWatermarkUploadProgress(0);
    }
  };

  // Handle cancel upload for watermark
  const handleCancelWatermarkUpload = () => {
    setSelectedWatermarkFile(null);
    setWatermarkPreviewUrl(null);
    setWatermarkError(null);
    setWatermarkSuccess(null);
    if (watermarkFileInputRef.current) {
      watermarkFileInputRef.current.value = '';
    }
  };

  return (
    <div className="cms-management">
      <h2>CMS Management</h2>
      
      {/* Promo Video Section */}
      <div className="cms-section">
        <div 
          className={`section-header ${!sectionsState.promoVideo ? 'active' : ''}`} 
          onClick={() => toggleSection('promoVideo')}
        >
          <h3>Promo Video</h3>
          <span className="toggle-icon">{sectionsState.promoVideo ? '▼' : '▲'}</span>
        </div>
        
        <div className={`section-content ${sectionsState.promoVideo ? 'collapsed' : ''}`}>
          <p className="section-description">
            Upload a new promo video to display on the homepage. The video will be processed and optimized automatically.
          </p>
          
          {videoError && <div className="error-message">{videoError}</div>}
          {videoSuccess && <div className="success-message">{videoSuccess}</div>}
          
          <div className="upload-container">
            <div className="file-input-container">
              <input
                type="file"
                ref={videoFileInputRef}
                onChange={handleVideoFileChange}
                accept="video/*"
                className="file-input"
                disabled={videoLoading}
              />
              <button 
                className="browse-button"
                onClick={() => videoFileInputRef.current?.click()}
                disabled={videoLoading}
              >
                Browse...
              </button>
              <div className="file-name">
                {selectedVideoFile ? selectedVideoFile.name : 'No file selected'}
              </div>
            </div>
            
            {selectedVideoFile && (
              <div className="video-preview-container">
                <h4>Preview</h4>
                <video 
                  src={videoPreviewUrl || undefined}
                  controls
                  className="video-preview"
                />
                
                <div className="upload-actions">
                  <button
                    className="upload-button"
                    onClick={handleVideoUpload}
                    disabled={videoLoading || !selectedVideoFile}
                  >
                    {videoLoading ? 'Uploading...' : 'Upload Video'}
                  </button>
                  
                  <button
                    className="cancel-button"
                    onClick={handleCancelVideoUpload}
                    disabled={videoLoading}
                  >
                    Cancel
                  </button>
                </div>
                
                {videoLoading && (
                  <div className="progress-container">
                    <div className="progress-bar">
                      <div 
                        className="progress-fill" 
                        style={{ width: `${videoUploadProgress}%` }}
                      ></div>
                    </div>
                    <div className="progress-text">{videoUploadProgress}%</div>
                  </div>
                )}
              </div>
            )}
          </div>
          
          <div className="cms-note">
            <p><strong>Note:</strong> The uploaded video will replace the current promo video on the homepage.</p>
            <p>Recommended video specs:</p>
            <ul>
              <li>Format: MP4</li>
              <li>Resolution: 1920x1080 (16:9)</li>
              <li>Duration: Less than 2 minutes</li>
              <li>Size: Less than 25MB</li>
            </ul>
          </div>
        </div>
      </div>

      {/* Background Image Section */}
      <div className="cms-section">
        <div 
          className={`section-header ${!sectionsState.backgroundImage ? 'active' : ''}`} 
          onClick={() => toggleSection('backgroundImage')}
        >
          <h3>Background Image</h3>
          <span className="toggle-icon">{sectionsState.backgroundImage ? '▼' : '▲'}</span>
        </div>
        
        <div className={`section-content ${sectionsState.backgroundImage ? 'collapsed' : ''}`}>
          <p className="section-description">
            Upload a new background image to use throughout the application. The image will be processed and optimized automatically.
          </p>
          
          {imageError && <div className="error-message">{imageError}</div>}
          {imageSuccess && <div className="success-message">{imageSuccess}</div>}
          
          <div className="upload-container">
            <div className="file-input-container">
              <input
                type="file"
                ref={imageFileInputRef}
                onChange={handleImageFileChange}
                accept="image/jpeg,image/png,image/webp"
                className="file-input"
                disabled={imageLoading}
              />
              <button 
                className="browse-button"
                onClick={() => imageFileInputRef.current?.click()}
                disabled={imageLoading}
              >
                Browse...
              </button>
              <div className="file-name">
                {selectedImageFile ? selectedImageFile.name : 'No file selected'}
              </div>
            </div>
            
            {selectedImageFile && (
              <div className="image-preview-container">
                <h4>Preview</h4>
                <img 
                  src={imagePreviewUrl || undefined}
                  alt="Background Preview"
                  className="image-preview"
                />
                
                <div className="upload-actions">
                  <button
                    className="upload-button"
                    onClick={handleImageUpload}
                    disabled={imageLoading || !selectedImageFile}
                  >
                    {imageLoading ? 'Uploading...' : 'Upload Image'}
                  </button>
                  
                  <button
                    className="cancel-button"
                    onClick={handleCancelImageUpload}
                    disabled={imageLoading}
                  >
                    Cancel
                  </button>
                </div>
                
                {imageLoading && (
                  <div className="progress-container">
                    <div className="progress-bar">
                      <div 
                        className="progress-fill" 
                        style={{ width: `${imageUploadProgress}%` }}
                      ></div>
                    </div>
                    <div className="progress-text">{imageUploadProgress}%</div>
                  </div>
                )}
              </div>
            )}
          </div>
          
          <div className="cms-note">
            <p><strong>Note:</strong> The uploaded image will replace the current background image used throughout the application.</p>
            <p>Recommended image specs:</p>
            <ul>
              <li>Format: JPEG, PNG, or WebP</li>
              <li>Resolution: 1920x1080 or higher</li>
              <li>Size: Less than 5MB</li>
            </ul>
          </div>
        </div>
      </div>

      {/* Watermark Image Section */}
      <div className="cms-section">
        <div 
          className={`section-header ${!sectionsState.watermarkImage ? 'active' : ''}`} 
          onClick={() => toggleSection('watermarkImage')}
        >
          <h3>Watermark Image</h3>
          <span className="toggle-icon">{sectionsState.watermarkImage ? '▼' : '▲'}</span>
        </div>
        
        <div className={`section-content ${sectionsState.watermarkImage ? 'collapsed' : ''}`}>
          <p className="section-description">
            Upload a new watermark image to use on videos. The image will be processed and optimized automatically.
          </p>
          
          {watermarkError && <div className="error-message">{watermarkError}</div>}
          {watermarkSuccess && <div className="success-message">{watermarkSuccess}</div>}
          
          <div className="upload-container">
            <div className="file-input-container">
              <input
                type="file"
                ref={watermarkFileInputRef}
                onChange={handleWatermarkFileChange}
                accept="image/png"
                className="file-input"
                disabled={watermarkLoading}
              />
              <button 
                className="browse-button"
                onClick={() => watermarkFileInputRef.current?.click()}
                disabled={watermarkLoading}
              >
                Browse...
              </button>
              <div className="file-name">
                {selectedWatermarkFile ? selectedWatermarkFile.name : 'No file selected'}
              </div>
            </div>
            
            {selectedWatermarkFile && (
              <div className="image-preview-container">
                <h4>Preview</h4>
                <div className="watermark-preview-wrapper">
                  <img 
                    src={watermarkPreviewUrl || undefined}
                    alt="Watermark Preview"
                    className="image-preview watermark-preview"
                  />
                </div>
                
                <div className="upload-actions">
                  <button
                    className="upload-button"
                    onClick={handleWatermarkUpload}
                    disabled={watermarkLoading || !selectedWatermarkFile}
                  >
                    {watermarkLoading ? 'Uploading...' : 'Upload Watermark'}
                  </button>
                  
                  <button
                    className="cancel-button"
                    onClick={handleCancelWatermarkUpload}
                    disabled={watermarkLoading}
                  >
                    Cancel
                  </button>
                </div>
                
                {watermarkLoading && (
                  <div className="progress-container">
                    <div className="progress-bar">
                      <div 
                        className="progress-fill" 
                        style={{ width: `${watermarkUploadProgress}%` }}
                      ></div>
                    </div>
                    <div className="progress-text">{watermarkUploadProgress}%</div>
                  </div>
                )}
              </div>
            )}
          </div>
          
          <div className="cms-note">
            <p><strong>Note:</strong> The uploaded image will replace the current watermark used on videos.</p>
            <p>Recommended image specs:</p>
            <ul>
              <li>Format: PNG only (for transparency)</li>
              <li>Resolution: 800x600 or similar</li>
              <li>Transparency: Include transparent background</li>
              <li>Size: Less than 5MB</li>
            </ul>
          </div>
        </div>
      </div>

      {/* Email Template Section */}
      <div className="cms-section">
        <div 
          className={`section-header ${!sectionsState.emailTemplate ? 'active' : ''}`} 
          onClick={() => toggleSection('emailTemplate')}
        >
          <h3>Email Template Settings</h3>
          <span className="toggle-icon">{sectionsState.emailTemplate ? '▼' : '▲'}</span>
        </div>
        
        <div className={`section-content ${sectionsState.emailTemplate ? 'collapsed' : ''}`}>
          <p className="section-description">
            Customize the text used in email templates sent to users after their purchase.
          </p>
          
          {emailConfigError && <div className="error-message">{emailConfigError}</div>}
          {emailConfigSuccess && <div className="success-message">{emailConfigSuccess}</div>}
          
          <div className="email-config-form">
            <div className="form-group">
              <label htmlFor="email-subject">Email Subject</label>
              <input
                id="email-subject"
                type="text"
                value={emailConfig.EMAIL_SUBJECT}
                onChange={(e) => handleEmailConfigChange('EMAIL_SUBJECT', e.target.value)}
                className="text-input"
                disabled={emailConfigLoading}
              />
            </div>
            
            <div className="form-group">
              <label htmlFor="email-heading">Email Heading</label>
              <input
                id="email-heading"
                type="text"
                value={emailConfig.EMAIL_HEADING}
                onChange={(e) => handleEmailConfigChange('EMAIL_HEADING', e.target.value)}
                className="text-input"
                disabled={emailConfigLoading}
              />
            </div>
            
            <div className="form-group">
              <label htmlFor="email-thank-you">Thank You Message</label>
              <textarea
                id="email-thank-you"
                value={emailConfig.EMAIL_THANK_YOU}
                onChange={(e) => handleEmailConfigChange('EMAIL_THANK_YOU', e.target.value)}
                className="text-area"
                rows={3}
                disabled={emailConfigLoading}
              />
            </div>
            
            <div className="form-group">
              <label htmlFor="email-instructions">Instructions Message</label>
              <textarea
                id="email-instructions"
                value={emailConfig.EMAIL_INSTRUCTIONS}
                onChange={(e) => handleEmailConfigChange('EMAIL_INSTRUCTIONS', e.target.value)}
                className="text-area"
                rows={3}
                disabled={emailConfigLoading}
              />
            </div>
            
            <div className="form-group">
              <label htmlFor="email-closing">Closing Message</label>
              <textarea
                id="email-closing"
                value={emailConfig.EMAIL_CLOSING}
                onChange={(e) => handleEmailConfigChange('EMAIL_CLOSING', e.target.value)}
                className="text-area"
                rows={3}
                disabled={emailConfigLoading}
              />
            </div>
            
            <div className="form-group">
              <label htmlFor="email-footer">Footer Message</label>
              <textarea
                id="email-footer"
                value={emailConfig.EMAIL_FOOTER}
                onChange={(e) => handleEmailConfigChange('EMAIL_FOOTER', e.target.value)}
                className="text-area"
                rows={2}
                disabled={emailConfigLoading}
              />
            </div>
            
            <div className="form-actions">
              <button
                type="button"
                className="save-button"
                onClick={handleSaveEmailConfig}
                disabled={emailConfigLoading}
              >
                {emailConfigLoading ? 'Saving...' : 'Save Email Settings'}
              </button>
            </div>
          </div>
          
          <div className="cms-note">
            <p><strong>Note:</strong> These settings control the content of emails sent to users after they purchase videos.</p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CMSManagement; 